'use client';

/**
 * Fades a background image in over whatever the host has already settled
 * beneath it. Replaces the former brush "swipe" reveal (TT-975): no SVG mask,
 * no stroke geometry, no content-box measurement — just a CSS opacity ramp held
 * at full via animation-fill-mode.
 *
 * Sits behind the symbols/text; the host renders the previously-settled
 * background(s) as a static layer underneath, so a new image fades in OVER the
 * old one (a crossfade), not over the default dark background.
 *
 * Tagged data-export-ignore: like the old masked SVG, the animating layer is
 * excluded from PNG export, which instead captures the static floor the host
 * stacks beneath (the settled background). Re-keyed by the host on background id
 * so a new background re-fades.
 */

// How long one background/piece takes to fade in. Deliberately independent of
// the piece scheduling cadence (REVEAL_DURATION_MS lives in TapestryCanvas), so
// changing the fade speed does not change how progressive pieces are paced.
export const FADE_DURATION_MS = 2250;

// Shared absolute-fill background style for the reveal layer AND the host's floor
// layer beneath it. They must stretch identically (backgroundSize) or the fading
// image and the settled floor misregister at handoff — so both spread this one
// object and only add their own `backgroundImage` (plus, here, the animation).
export const FILL_LAYER_STYLE = {
  position: 'absolute',
  inset: 0,
  backgroundSize: '100% 100%', // stretch to fill; matches the canvas aspect
  pointerEvents: 'none',
};

export default function BackgroundReveal({ imageUrl }) {
  if (!imageUrl) return null;
  return (
    <div
      // See export note above — the animating layer never enters the PNG.
      data-export-ignore
      className="tapestry-bg-fade"
      style={{
        ...FILL_LAYER_STYLE,
        backgroundImage: `url(${imageUrl})`,
        animationDuration: `${FADE_DURATION_MS}ms`,
      }}
    />
  );
}
